import React, { SyntheticEvent, useCallback, useEffect } from "react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import CardTrainingManagementUX from "./CardTrainingManagementUX";
import TableTrainingManagement from "./TableTrainingManagement";

import {
  ACTIONS,
  BTN_ACTS,
  LIST_LIMIT,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
  TrainingAttendanceFilterType,
  UpdateStatusParamsType,
} from "./sequence/TrainingManagement";

import { ATTENDANCE_STATUS_OPTIONS, COURSE_TYPE_OPTIONS } from "./HRMInterface";

// Types
type CardTrainingManagementProps = Pick<
  PickedState,
  "buttonLoadCheck" | "searchedItemListWithKey"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  TrainingManagementSequence: State["TrainingManagementSequence"];
  defaultFilter?: TrainingAttendanceFilterType;
  languageUX: "en" | "th";
  divisionOptions: Record<string, any>[];
};

const CardTrainingManagement = (props: CardTrainingManagementProps) => {
  useEffect(() => {
    props.runSequence({
      sequence: "TrainingManagement",
      filter: props.defaultFilter,
      restart: true,
    });
  }, []);

  const mapOptions = useCallback(
    (items?: Record<string, any>[]) =>
      (items ?? []).map((item) => ({
        key: item.id,
        text: item.subject,
        value: item.id,
      })),
    []
  );

  const handleSearch = () => {
    props.runSequence({
      sequence: "TrainingManagement",
      action: ACTIONS.SEARCH,
      activePage: props.TrainingManagementSequence?.pagination?.activePage,
    });
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const filter = { ...props.TrainingManagementSequence?.filter };

    if (name === "division_id") {
      filter.employee_id = null;
    }

    props.setProp("TrainingManagementSequence.filter", {
      ...filter,
      [name]: data.value,
    });
  };

  const handleUpdateStatus = (data: UpdateStatusParamsType) => {
    props.runSequence({ sequence: "TrainingManagement", action: ACTIONS.UPDATE_STATUS, ...data });
  };

  const handleDropdownBlur = async (e: any, data: any) => {
    props.setProp("TrainingManagementSequence.filter.course_id", data.value);
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({ sequence: "TrainingManagement", action: ACTIONS.SEARCH, activePage });
  };

  return (
    <div style={{ padding: "0 10px" }}>
      <CardTrainingManagementUX
        filter={props.TrainingManagementSequence?.filter}
        languageUX={props.languageUX}
        divisionOptions={props.divisionOptions}
        statusOptions={ATTENDANCE_STATUS_OPTIONS}
        typeOptions={COURSE_TYPE_OPTIONS}
        onChangeFilter={handleChangeFilter}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        courseSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="TKC"
            defaultText={props.TrainingManagementSequence?.filter?.course_id}
            placeholder="Search Course"
            selectedItem={props.TrainingManagementSequence?.filter?.course_id ?? null}
            type="TrainingCourse"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            mapOptions={mapOptions}
            setSelected={(value) => {
              props.setProp("TrainingManagementSequence.filter.course_id", value);
            }}
            onBlur={handleDropdownBlur}
          />
        }
        employeeSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="TKE"
            defaultText={props.TrainingManagementSequence?.filter?.employee_name}
            placeholder="Search Employee"
            selectedItem={props.TrainingManagementSequence?.filter?.employee_id ?? null}
            type="UserEmployee"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value) => {
              props.setProp("TrainingManagementSequence.filter", {
                ...props.TrainingManagementSequence?.filter,
                employee_id: value,
                employee_name: "",
              });
            }}
            onAdditionalUrlParams={() => ({
              affiliate: props.TrainingManagementSequence?.filter?.division_id,
            })}
          />
        }
      />

      <TableTrainingManagement
        data={props.TrainingManagementSequence?.pagination?.items}
        buttonLoadCheck={props.buttonLoadCheck}
        onUpdateStatus={handleUpdateStatus}
      />

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.75rem" }}>
        <PaginationShorthand
          activePage={props.TrainingManagementSequence?.pagination?.activePage ?? 1}
          limit={LIST_LIMIT}
          total={props.TrainingManagementSequence?.pagination?.total}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

CardTrainingManagement.displayName = "CardTrainingManagement";

export default React.memo(CardTrainingManagement);
